import { FC, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { VideoParticipant } from '../VideoParticipant/VideoParticipant';
import type { Participant } from 'twilio-video';

interface VideoGridProps {
  localParticipant: Participant | undefined;
  remoteParticipants: Participant[];
  isSpeakerEnabled: boolean;
  isConnecting: boolean;
  isInstructor: boolean;
  errorSlot?: ReactNode;
}

export const VideoGrid: FC<VideoGridProps> = ({
  localParticipant,
  remoteParticipants,
  isSpeakerEnabled,
  isConnecting,
  isInstructor,
  errorSlot,
}) => {
  const hasMultipleParticipants = remoteParticipants.length > 0;

  return (
    <div
      id="video-grid"
      className="p-2 sm:p-3 flex flex-row gap-2 sm:gap-3 h-[200px] sm:h-[280px] md:h-[340px] lg:h-[540px] overflow-hidden"
    >
      {localParticipant && (
        <VideoParticipant
          participant={localParticipant}
          isLocal
          videoId="local-video"
          className="flex-1 min-w-0 h-full"
        />
      )}

      {hasMultipleParticipants &&
        remoteParticipants.map((participant, index) => (
          <VideoParticipant
            key={participant.sid}
            participant={participant}
            isSpeakerEnabled={isSpeakerEnabled}
            videoId={index === 0 ? 'remote-video' : undefined}
            className="flex-1 min-w-0 h-full"
          />
        ))}

      {!hasMultipleParticipants &&
        (errorSlot ?? (
          <div className="flex-1 min-w-0 h-full flex items-center justify-center bg-neutral-900 rounded-lg">
            <div className="text-center">
              {isConnecting && (
                <Loader2 className="w-8 h-8 animate-spin text-neutral-400 mx-auto" />
              )}
              <p className="text-neutral-400 text-sm mt-2">
                {isInstructor
                  ? 'Waiting for student to connect...'
                  : 'Waiting for instructor to connect...'}
              </p>
            </div>
          </div>
        ))}
    </div>
  );
};
